import React, { useState, useEffect, useCallback } from "react"
import SearchModal from "./modals/SearchModal"
import AddNodeModal from "./modals/AddNodeModal"
import MoveNodeModal from "./modals/MoveNodeModal"
import NodePropertiesPanel from "./modals/NodePropertiesPanel"
import { initialCategories, initialRelations } from "./utils"
import { useCategoryNavigation } from "./hooks/useCategoryNavigation"
import { useCategorySelection } from "./hooks/useCategorySelection"
import { useCategoryOperations } from "./hooks/useCategoryOperations"
import { useDragAndDrop } from "./hooks/useDragAndDrop"
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts"
import { useCategoryData } from "./hooks/useCategoryData"

// Simple self-contained toast notifications
function useToast() {
  const [toasts, setToasts] = useState([])
  const notify = useCallback((message, type = "info") => {
    const id = Date.now()
    setToasts((prev) => [...prev, { id, message, type }])
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000)
  }, [])
  return { toasts, notify }
}

export default function CategoryManager({
  initialData = {
    categories: initialCategories,
    relations: initialRelations,
    rootCategory: { id: 1, name: "Taxonomy" },
  },
  operations = {},
  operationInProgress = false,
  onReload,
} = {}) {
  const { toasts, notify } = useToast()
  const [categories, setCategories] = useState(initialData.categories)
  const [relations, setRelations] = useState(initialData.relations)
  const [breadcrumb, setBreadcrumb] = useState([initialData.rootCategory])
  const [showSearch, setShowSearch] = useState(false)
  const [showAddNode, setShowAddNode] = useState(false)
  const [showMoveNode, setShowMoveNode] = useState(false)
  const [showRename, setShowRename] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [propertiesSlug, setPropertiesSlug] = useState(null)
  const [modalJustClosed, setModalJustClosed] = useState(false)

  useEffect(() => {
    setCategories(initialData.categories)
    setRelations(initialData.relations)
    setBreadcrumb((prev) => {
      if (prev.length === 0 || prev[0].id !== initialData.rootCategory.id)
        return [initialData.rootCategory]
      return prev
    })
  }, [initialData])

  const currentCategory = breadcrumb[breadcrumb.length - 1]

  const { children, getParents, getChildrenCount } = useCategoryData(
    categories, relations, currentCategory
  )

  const { selectedItems, setSelectedItems, handleCardClick, handleBackgroundClick } =
    useCategorySelection(children)

  const { navigateInto, navigateTo, jumpToCategory } = useCategoryNavigation(
    breadcrumb, setBreadcrumb, setSelectedItems, categories, relations
  )

  const {
    createNewCategory, bulkCreateCategories, renameCategory,
    addExistingAsChild, moveSelectedToParent, deleteSelected,
    getCategoryById, wouldCreateCycle,
  } = useCategoryOperations(
    categories, setCategories, relations, setRelations, currentCategory,
    breadcrumb, setBreadcrumb, selectedItems, setSelectedItems,
    setModalJustClosed, notify, operations
  )

  const {
    dragOverId, handleDragStart, handleDragOver, handleDragEnter,
    handleDragLeave, handleDrop, handleDragEnd,
  } = useDragAndDrop(selectedItems, setSelectedItems, getCategoryById, moveSelectedToParent)

  useEffect(() => {
    if (!modalJustClosed) return
    const t = setTimeout(() => setModalJustClosed(false), 100)
    return () => clearTimeout(t)
  }, [modalJustClosed])

  const handleOpenProperties = useCallback(() => {
    if (selectedItems.length !== 1 || selectedItems.includes(1)) return
    const cat = getCategoryById(selectedItems[0])
    if (cat?.slug) setPropertiesSlug(cat.slug)
  }, [selectedItems, getCategoryById])

  useKeyboardShortcuts({
    selectedItems, categories, breadcrumb, showSearch,
    showAddNode, showMoveNode, showRename, modalJustClosed,
    setBreadcrumb, setSelectedItems, setShowRename, setShowAddNode,
    setShowSearch, handleOpenProperties, children,
  })

  const canActOnSelected = selectedItems.length > 0 && !selectedItems.includes(1)
  const canActOnSingle = selectedItems.length === 1 && !selectedItems.includes(1)

  return (
    <div className="flex flex-col h-full bg-admin_canvas select-none">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-admin_bordered flex-shrink-0">
        <div className="flex items-center gap-3">
          <h1 className="text-admin_text font-semibold text-base">Taxonomy</h1>
          {operationInProgress && (
            <span className="flex items-center gap-1.5 text-xs text-admin_text_dim">
              <span className="w-3 h-3 border-2 border-admin_primary border-t-transparent rounded-full animate-spin" />
              Saving...
            </span>
          )}
        </div>
        <button
          onClick={() => setShowSearch(true)}
          disabled={categories.length <= 1}
          className="flex items-center gap-1.5 text-xs text-admin_text_dim hover:text-admin_text disabled:opacity-40 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          Search (S)
        </button>
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 px-6 py-2.5 bg-admin_paper/40 border-b border-admin_bordered flex-shrink-0 flex-wrap">
        {breadcrumb.map((item, index) => (
          <React.Fragment key={`${item.id}-${index}`}>
            {index > 0 && (
              <span className="text-admin_text_dim text-xs">›</span>
            )}
            <button
              onClick={(e) => {
                const isCurrent = index === breadcrumb.length - 1
                if (e.shiftKey || e.metaKey || e.ctrlKey) handleCardClick(e, item.id)
                else if (isCurrent) handleCardClick(e, item.id)
                else navigateTo(index)
              }}
              onDragOver={(e) => handleDragOver(e, item.id)}
              onDragEnter={(e) => handleDragEnter(e, item.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, item.id)}
              className={`text-sm px-2 py-0.5 rounded transition-colors ${
                index === breadcrumb.length - 1
                  ? "text-admin_text font-medium"
                  : "text-admin_text_dim hover:text-admin_text"
              } ${dragOverId === item.id ? "bg-admin_primary/20 text-admin_primary" : ""}
              ${selectedItems.includes(item.id) ? "text-admin_primary" : ""}`}
            >
              {index === 0 ? "◎ " : ""}{item.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between px-6 py-2.5 border-b border-admin_bordered flex-shrink-0">
        <span className="text-xs text-admin_text_dim">
          {children.length === 0 ? "No children" : `${children.length} children`}
          {(() => {
            const parents = getParents(currentCategory.id)
            return currentCategory.id !== 1 && parents.length > 1
              ? ` · ${parents.length} parents`
              : null
          })()}
          {selectedItems.length > 0 && (
            <span className="text-admin_primary"> · {selectedItems.length} selected</span>
          )}
        </span>

        <div className="flex items-center gap-1">
          {canActOnSingle && (
            <>
              <ToolbarBtn onClick={() => setShowRename(true)}>Rename (F2)</ToolbarBtn>
              <ToolbarBtn onClick={handleOpenProperties}>Properties (Space)</ToolbarBtn>
            </>
          )}
          {canActOnSelected && (
            <>
              <ToolbarBtn onClick={() => setShowMoveNode(true)}>
                Move{selectedItems.length > 1 ? ` (${selectedItems.length})` : ""}
              </ToolbarBtn>
              <ToolbarBtn
                onClick={() => setShowDeleteConfirm(true)}
                danger
              >
                Remove{selectedItems.length > 1 ? ` (${selectedItems.length})` : ""}
              </ToolbarBtn>
              <div className="w-px h-4 bg-admin_bordered mx-1" />
            </>
          )}
          <ToolbarBtn primary onClick={() => setShowAddNode(true)}>
            + Add
          </ToolbarBtn>
        </div>
      </div>

      {/* Grid */}
      <div
        className="flex-1 overflow-y-auto p-6 taxonomy-grid"
        style={children.length === 0 ? { display: "flex", alignItems: "center", justifyContent: "center" } : { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 280px))", gap: "16px", alignContent: "start" }}
        onClick={handleBackgroundClick}
      >
        {children.length === 0 ? (
          <div className="taxonomy-empty flex flex-col items-center justify-center text-center gap-4">
            <div className="text-4xl opacity-30">◫</div>
            <p className="text-admin_text_dim text-sm">
              No children in "{currentCategory.name}"
            </p>
            <button
              onClick={() => setShowAddNode(true)}
              className="text-sm text-admin_primary hover:opacity-80 transition-opacity"
            >
              + Add first node
            </button>
          </div>
        ) : (
          children.map((child) => {
            const parents = getParents(child.id)
            const childCount = getChildrenCount(child.id)
            const isSelected = selectedItems.includes(child.id)
            const isDragOver = dragOverId === child.id

            return (
              <div
                key={child.id}
                className={`taxonomy-card relative rounded-xl border bg-admin_paper cursor-pointer transition-all
                  ${isSelected
                    ? "border-admin_primary ring-1 ring-admin_primary/30 bg-admin_primary/5"
                    : "border-admin_bordered hover:border-admin_primary/50"
                  }
                  ${isDragOver ? "border-admin_primary ring-2 ring-admin_primary/40 scale-[1.02]" : ""}
                `}
                draggable
                onClick={(e) => handleCardClick(e, child.id)}
                onDoubleClick={() => navigateInto(child)}
                onDragStart={(e) => handleDragStart(e, child.id)}
                onDragOver={(e) => handleDragOver(e, child.id)}
                onDragEnter={(e) => handleDragEnter(e, child.id)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, child.id)}
                onDragEnd={handleDragEnd}
              >
                {/* Color strip */}
                <div
                  className="absolute top-0 left-0 right-0 h-1 rounded-t-xl"
                  style={{ backgroundColor: child.color }}
                />

                <div className="pt-4 px-4 pb-3">
                  <div className="flex items-start gap-2 mb-1">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5"
                      style={{ backgroundColor: child.color }}
                    />
                    <span className="text-admin_text text-sm font-medium leading-snug">
                      {child.name}
                    </span>
                  </div>
                  <div className="text-admin_text_dim text-xs pl-4">{child.slug}</div>

                  {(childCount > 0 || parents.length > 1) && (
                    <div className="flex gap-3 mt-2 pl-4">
                      {childCount > 0 && (
                        <span className="text-xs text-admin_text_dim">
                          ↓ {childCount}
                        </span>
                      )}
                      {parents.length > 1 && (
                        <span className="text-xs text-admin_text_dim">
                          ⇢ {parents.length} parents
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between px-6 py-2.5 border-t border-admin_bordered flex-shrink-0">
        <div className="flex gap-4 text-xs text-admin_text_dim">
          <span>{categories.length - 1} total nodes</span>
          <span>{relations.length} relations</span>
        </div>
        <span className="text-xs text-admin_text_dim hidden lg:block">
          Arrows · Enter=open · Backspace=back · F2=rename · Space=properties · A=add · S=search · Drag=move
        </span>
      </div>

      {/* Toast notifications */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`px-4 py-2.5 rounded-lg text-sm shadow-lg text-white ${
                t.type === "error"
                  ? "bg-red-600"
                  : t.type === "warning"
                  ? "bg-amber-500"
                  : "bg-admin_primary"
              }`}
            >
              {t.message}
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      <SearchModal
        isOpen={showSearch}
        onClose={() => { setShowSearch(false); setModalJustClosed(true) }}
        categories={categories}
        relations={relations}
        onSelect={jumpToCategory}
      />

      <AddNodeModal
        isOpen={showAddNode}
        onClose={() => { setShowAddNode(false); setModalJustClosed(true) }}
        onCreateNew={createNewCategory}
        onBulkCreate={bulkCreateCategories}
        onSelectExisting={addExistingAsChild}
        categories={categories}
        excludeIds={[currentCategory.id, ...children.map((c) => c.id)]}
        parentName={currentCategory.name}
        currentCategoryId={currentCategory.id}
        wouldCreateCycle={wouldCreateCycle}
      />

      <MoveNodeModal
        isOpen={showMoveNode}
        onClose={() => { setShowMoveNode(false); setModalJustClosed(true) }}
        categories={categories}
        onSelect={moveSelectedToParent}
        excludeIds={[
          ...selectedItems,
          ...(selectedItems.includes(currentCategory.id) && breadcrumb.length > 1
            ? [breadcrumb[breadcrumb.length - 2].id]
            : [currentCategory.id]),
        ]}
        selectedCount={selectedItems.length}
        selectedNames={selectedItems.map((id) => getCategoryById(id)?.name).filter(Boolean)}
      />

      {/* Inline rename — keeps it lightweight */}
      {showRename && selectedItems.length === 1 && (() => {
        const cat = getCategoryById(selectedItems[0])
        if (!cat) return null
        return (
          <RenameInlineModal
            category={cat}
            onClose={() => { setShowRename(false); setModalJustClosed(true) }}
            onRename={renameCategory}
          />
        )
      })()}

      {/* Delete confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowDeleteConfirm(false)}>
          <div
            className="w-[400px] bg-admin_paper rounded-2xl border border-admin_bordered shadow-2xl p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-admin_text font-semibold text-sm">
              Remove {selectedItems.length > 1 ? `${selectedItems.length} nodes` : `"${getCategoryById(selectedItems[0])?.name}"`}?
            </h3>
            <p className="text-admin_text_dim text-sm">
              This will permanently delete {selectedItems.length > 1 ? "these nodes" : "this node"} and all their relations.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setShowDeleteConfirm(false); setModalJustClosed(true) }}
                className="text-sm text-admin_text_dim hover:text-admin_text px-3 py-1.5"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowDeleteConfirm(false); setModalJustClosed(true); deleteSelected(navigateTo) }}
                className="text-sm bg-red-600 text-white px-4 py-1.5 rounded-lg hover:opacity-90"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Properties side panel */}
      {propertiesSlug && (
        <NodePropertiesPanel
          slug={propertiesSlug}
          onClose={() => setPropertiesSlug(null)}
          onSaved={() => { setPropertiesSlug(null); onReload?.() }}
        />
      )}
    </div>
  )
}

function ToolbarBtn({ children, onClick, danger, primary }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
        primary
          ? "bg-admin_primary text-white hover:opacity-90"
          : danger
          ? "text-red-400 hover:bg-red-500/10"
          : "text-admin_text_dim hover:text-admin_text hover:bg-admin_canvas"
      }`}
    >
      {children}
    </button>
  )
}

function RenameInlineModal({ category, onClose, onRename }) {
  const [name, setName] = useState(category.name)
  const ref = React.useRef(null)

  useEffect(() => {
    setTimeout(() => { ref.current?.focus(); ref.current?.select() }, 50)
  }, [])

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose()
      if (e.key === "Enter" && e.target === ref.current) { e.preventDefault(); handleSave() }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [name])

  const handleSave = () => {
    if (name.trim()) { onRename(name.trim(), category.color); onClose() }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div
        className="w-[400px] bg-admin_paper rounded-2xl border border-admin_bordered shadow-2xl p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-admin_text font-semibold text-sm">Rename "{category.name}"</h3>
        <input
          ref={ref}
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-admin_canvas border border-admin_bordered rounded-lg px-3 py-2 text-admin_text text-sm focus:outline-none focus:border-admin_primary"
        />
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="text-sm text-admin_text_dim hover:text-admin_text px-3 py-1.5">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            className="text-sm bg-admin_primary text-white px-4 py-1.5 rounded-lg disabled:opacity-40 hover:opacity-90"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
